import Phaser from "phaser";
import mapConfig from "../game/data/mapConfig.json";
import type { LaneStateSummary } from "../../../shared/types/BattleEvent";
import type { Language } from "../i18n";
import { t, threatLabel } from "../i18n";

export class LaneStatusPanel {
  private readonly rows = new Map<number, { dot: Phaser.GameObjects.Rectangle; text: Phaser.GameObjects.Text; detail: Phaser.GameObjects.Text; threat: Phaser.GameObjects.Text }>();
  private readonly panel: Phaser.GameObjects.Image;
  private readonly toggleText: Phaser.GameObjects.Text;
  private expanded = false;

  constructor(scene: Phaser.Scene, private readonly language: Language, private readonly uiScale = 1, private readonly highContrastText = false) {
    this.panel = scene.add.image(142, 42, "lane_panel").setDepth(125).setScrollFactor(0).setDisplaySize(226, 54).setAlpha(0.58);
    scene.add
      .text(28, 24, t(this.language, "lanePressure"), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.fontSize(13),
        color: "#f6f0df",
        stroke: "#111016",
        strokeThickness: this.stroke(2)
      })
      .setResolution(2)
      .setDepth(127)
      .setScrollFactor(0);
    const toggleBg = scene.add.rectangle(226, 31, 28, 22, 0x071118, 0.62).setStrokeStyle(1, 0x8de7ff, 0.5).setDepth(128).setScrollFactor(0);
    this.toggleText = scene.add
      .text(226, 31, "+", {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", sans-serif",
        fontSize: this.fontSize(13),
        color: "#f7e9bb",
        stroke: "#08070a",
        strokeThickness: this.stroke(1)
      })
      .setResolution(2)
      .setOrigin(0.5)
      .setDepth(129)
      .setScrollFactor(0);
    toggleBg.setInteractive({ useHandCursor: true });
    toggleBg.on("pointerdown", () => this.setExpanded(!this.expanded));
    this.toggleText.setInteractive({ useHandCursor: true });
    this.toggleText.on("pointerdown", () => this.setExpanded(!this.expanded));

    mapConfig.lanes.forEach((lane, index) => {
      const y = 50 + index * 17;
      const dot = scene.add.rectangle(34, y + 6, 8, 8, 0x5edb73, 0.95).setDepth(127).setScrollFactor(0);
      const text = scene.add
        .text(46, y, `${lane.lane}. ${this.laneName(lane.name)}`, {
          fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
          fontSize: this.fontSize(12),
          color: "#f6f0df",
          stroke: "#111016",
          strokeThickness: this.stroke(1),
          fixedWidth: 150
        })
        .setResolution(2)
        .setDepth(127)
        .setScrollFactor(0);
      const threat = scene.add
        .text(190, y, threatLabel(this.language, "none"), {
          fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
          fontSize: this.fontSize(12),
          color: "#aee7b0",
          stroke: "#111016",
          strokeThickness: this.stroke(1),
          fixedWidth: 48
        })
        .setResolution(2)
        .setDepth(127)
        .setScrollFactor(0);
      const detail = scene.add
        .text(242, y, "0/0", {
          fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
          fontSize: this.fontSize(12),
          color: "#dff9ff",
          stroke: "#111016",
          strokeThickness: this.stroke(1),
          fixedWidth: 54
        })
        .setResolution(2)
        .setDepth(127)
        .setScrollFactor(0);
      this.rows.set(lane.lane, { dot, text, detail, threat });
    });
    this.setExpanded(false);
  }

  updateLaneStates(states: LaneStateSummary[]) {
    for (const state of states) {
      const row = this.rows.get(state.lane);
      if (!row) continue;
      const riskLevel = state.riskLevel ?? this.riskFromPressure(state.enemyPressureLevel);
      const color = this.colorFor(riskLevel);
      const outpost = state.economyOutpostOwner;
      row.dot.setFillStyle(color);
      row.threat.setText(threatLabel(this.language, riskLevel));
      row.threat.setColor(`#${color.toString(16).padStart(6, "0")}`);
      row.detail.setText(`${this.unitCountLabel(state.playerUnitCount ?? 0, state.enemyUnitCount ?? 0)}${this.outpostLabel(outpost)}`);
    }
  }

  private colorFor(level: NonNullable<LaneStateSummary["riskLevel"]>) {
    switch (level) {
      case "collapse":
        return 0xff604f;
      case "danger":
        return 0xffb15a;
      case "contested":
        return 0xf3d46e;
      case "watch":
        return 0x83ecff;
      case "safe":
      default:
        return 0x6fe086;
    }
  }

  private riskFromPressure(level: LaneStateSummary["enemyPressureLevel"]): NonNullable<LaneStateSummary["riskLevel"]> {
    if (level === "critical") return "collapse";
    if (level === "high") return "danger";
    if (level === "medium") return "contested";
    if (level === "low") return "watch";
    return "safe";
  }

  private setExpanded(expanded: boolean) {
    this.expanded = expanded;
    this.panel.setPosition(142, expanded ? 82 : 42);
    this.panel.setDisplaySize(expanded ? 304 : 226, expanded ? 136 : 54);
    this.panel.setAlpha(expanded ? 0.64 : 0.48);
    this.toggleText.setText(expanded ? "-" : "+");
    for (const row of this.rows.values()) {
      row.dot.setVisible(expanded);
      row.text.setVisible(expanded);
      row.detail.setVisible(expanded);
      row.threat.setVisible(expanded);
    }
  }

  private unitCountLabel(playerUnits: number, enemyUnits: number) {
    return this.language === "ko" ? `아${playerUnits}/적${enemyUnits}` : `P${playerUnits}/E${enemyUnits}`;
  }

  private outpostLabel(owner: LaneStateSummary["economyOutpostOwner"]) {
    if (owner === "player") return " $+";
    if (owner === "enemy") return " $-";
    return "";
  }

  private laneName(name: string) {
    if (this.language === "en") return name;
    switch (name) {
      case "North Wall Road":
        return "북쪽 성벽로";
      case "Upper Forest Path":
        return "상단 숲길";
      case "Central Grand Road":
        return "중앙 대로";
      case "Lower Ruins Path":
        return "하단 폐허길";
      case "Southern Marsh Trail":
        return "남쪽 늪길";
      default:
        return name;
    }
  }

  private fontSize(base: number) {
    return `${Math.round(base * this.uiScale)}px`;
  }

  private stroke(base: number) {
    return this.highContrastText ? Math.max(2, base + 1) : base;
  }
}
